import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Container,
    Typography,
    TextField,
    Grid,
    Chip,
    IconButton,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    CircularProgress,
    Alert,
    Card,
    Button,
    useTheme,
    useMediaQuery,
    alpha,
    Tooltip,
    TablePagination,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Autocomplete,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import InventoryIcon from '@mui/icons-material/Inventory';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { packagesService } from '../../../../services/api/packages';
import { schoolsService } from '../../../../services/api/schools';
import { Package as PackageType, PackagesQueryParams } from '../../../../types/package';
import { format } from 'date-fns';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import { Box as MuiBox, BoxProps } from '@mui/material';

const filterContainerStyles = (theme: any) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    marginBottom: 3,
    padding: 3,
    borderRadius: 2,
    background: `linear-gradient(165deg, 
        ${alpha(theme.palette.background.default, 0.95)} 0%, 
        ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
    backdropFilter: 'blur(10px)',
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`,
    '& .filter-header': {
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        marginBottom: 1,
        '& .MuiTypography-root': {
            fontWeight: 600,
            color: theme.palette.primary.main,
        },
    },
    '& .filter-content': {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 2,
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
        },
    },
    '& .filter-group': {
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        minWidth: 200,
        flex: 1,
        '& .MuiFormControl-root': {
            width: '100%',
        },
        '& .MuiInputLabel-root': {
            color: theme.palette.text.secondary,
        },
        '& .MuiOutlinedInput-root': {
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            '&:hover': {
                backgroundColor: alpha(theme.palette.background.paper, 0.9),
            },
            '&.Mui-focused': {
                backgroundColor: alpha(theme.palette.background.paper, 1),
            },
        },
    },
    '& .filter-actions': {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 1,
        marginTop: 1,
        paddingTop: 2,
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    },
});

const tableStyles = {
    borderRadius: (theme: any) => theme.spacing(2),
    background: (theme: any) => `linear-gradient(165deg, 
        ${alpha(theme.palette.background.default, 0.92)} 0%, 
        ${alpha(theme.palette.background.paper, 0.96)} 100%)`,
    backdropFilter: 'blur(10px)',
    border: (theme: any) => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    boxShadow: (theme: any) => `0 4px 20px ${alpha(theme.palette.common.black, 0.08)}`,
    '& .MuiTableHead-root': {
        background: (theme: any) => alpha(theme.palette.primary.main, 0.05),
        '& .MuiTableCell-head': {
            fontWeight: 600,
            color: (theme: any) => theme.palette.primary.main,
            borderBottom: (theme: any) => `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        },
    },
    '& .MuiTableBody-root': {
        '& .MuiTableRow-root': {
            transition: 'background-color 0.2s ease',
            '&:hover': {
                backgroundColor: (theme: any) => alpha(theme.palette.primary.main, 0.04),
            },
        },
    },
};

// Add type definition for package status
type PackageStatus = 'PREPARED' | 'DELIVERED' | 'PENDING';

interface School {
    id: number;
    name: string;
}

const StyledBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4),
    gap: theme.spacing(2),
    minHeight: '200px',
    background: `linear-gradient(165deg, 
        ${alpha(theme.palette.background.default, 0.85)} 0%, 
        ${alpha(theme.palette.background.paper, 0.92)} 100%)`,
    borderRadius: theme.spacing(2),
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`,
}));

const StyledOverlay = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: alpha(theme.palette.background.paper, 0.7),
    backdropFilter: 'blur(4px)',
    zIndex: 1,
}));

export const PackagesScreen: React.FC = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [packages, setPackages] = useState<PackageType[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [totalPackages, setTotalPackages] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [status, setStatus] = useState<'PREPARED' | 'DELIVERED' | 'PENDING' | ''>('');
    const [selectedSchool, setSelectedSchool] = useState<number | ''>('');
    const [schools, setSchools] = useState<School[]>([]);
    const [loadingSchools, setLoadingSchools] = useState(false);
    const [dateRange, setDateRange] = useState<{ start: Date | null; end: Date | null }>({
        start: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
        end: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0),
    });
    const [isFiltering, setIsFiltering] = useState(false);

    // Fetch schools list
    const fetchSchools = async () => {
        setLoadingSchools(true);
        try {
            const response = await schoolsService.getSchoolsList();
            if (response.status === 'success') {
                setSchools(response.data);
            }
        } catch (err) {
            console.error('Error fetching schools:', err);
        } finally {
            setLoadingSchools(false);
        }
    };

    useEffect(() => {
        fetchSchools();
    }, []);

    const fetchPackages = async (params: PackagesQueryParams) => {
        setLoading(true);
        setError(null);
        try {
            const response = await packagesService.getPackages({
                ...params,
                page: (params.page || 0) + 1,
                per_page: params.per_page,
                status: params.status === '' ? '' : params.status as 'PREPARED' | 'DELIVERED',
                date_range: params.date_range,
                school_id: params.school_id,
            });
            
            if (response.status === 'success') {
                console.log('First package data:', response.data.data[0]); // Log first package
                console.log('School data:', response.data.data[0]?.school); // Log school data specifically
                console.log('School name:', response.data.data[0]?.school?.name); // Log school name
                setPackages(response.data.data);
                setTotalPackages(response.data.total);
            } else {
                setError(response.message || t('packages.errors.fetchFailed'));
            }
        } catch (err) {
            setError(t('packages.errors.fetchFailed'));
            console.error('Error fetching packages:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleStatusChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        const newStatus = event.target.value as 'PREPARED' | 'DELIVERED' | 'PENDING' | '';
        setStatus(newStatus);
        setPage(0);
        setIsFiltering(true);
        fetchPackages({
            page: 0,
            per_page: rowsPerPage,
            status: newStatus === '' ? '' : newStatus as 'PREPARED' | 'DELIVERED',
            date_range: dateRange.start && dateRange.end ? {
                start: format(dateRange.start, 'yyyy-MM-dd'),
                end: format(dateRange.end, 'yyyy-MM-dd'),
            } : undefined,
            school_id: selectedSchool,
        }).finally(() => setIsFiltering(false));
    };

    const handleSchoolChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        const newSchoolId = event.target.value as number | '';
        setSelectedSchool(newSchoolId);
        setPage(0);
        setIsFiltering(true);
        fetchPackages({
            page: 0,
            per_page: rowsPerPage,
            status: status === '' ? '' : status as 'PREPARED' | 'DELIVERED',
            date_range: dateRange.start && dateRange.end ? {
                start: format(dateRange.start, 'yyyy-MM-dd'),
                end: format(dateRange.end, 'yyyy-MM-dd'),
            } : undefined,
            school_id: newSchoolId,
        }).finally(() => setIsFiltering(false));
    };

    const handleDateRangeChange = (field: 'start' | 'end', value: Date | null) => {
        const newDateRange = { ...dateRange, [field]: value };
        setDateRange(newDateRange);
        if (newDateRange.start && newDateRange.end) {
            setPage(0);
            setIsFiltering(true);
            fetchPackages({
                page: 0,
                per_page: rowsPerPage,
                status: status as 'PREPARED' | 'DELIVERED',
                date_range: {
                    start: format(newDateRange.start, 'yyyy-MM-dd'),
                    end: format(newDateRange.end, 'yyyy-MM-dd'),
                },
                school_id: selectedSchool || undefined,
            }).finally(() => setIsFiltering(false));
        }
    };

    useEffect(() => {
        fetchPackages({
            page,
            per_page: rowsPerPage,
            status: status === '' ? '' : status as 'PREPARED' | 'DELIVERED',
            date_range: dateRange.start && dateRange.end ? {
                start: format(dateRange.start, 'yyyy-MM-dd'),
                end: format(dateRange.end, 'yyyy-MM-dd'),
            } : undefined,
            school_id: selectedSchool,
        });
    }, [page, rowsPerPage, status, dateRange, selectedSchool]);

    const renderEmptyState = () => {
        const hasActiveFilters = status !== '' || selectedSchool !== '' || 
            (dateRange.start && dateRange.end && 
             (dateRange.start.getTime() !== new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime() ||
              dateRange.end.getTime() !== new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getTime()));

        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 8,
                    textAlign: 'center',
                    background: (theme) => `linear-gradient(165deg, 
                        ${alpha(theme.palette.background.default, 0.85)} 0%, 
                        ${alpha(theme.palette.background.paper, 0.92)} 100%)`,
                    borderRadius: 3,
                    border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    boxShadow: (theme) => `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`,
                    margin: (theme) => theme.spacing(4, 0),
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: (theme) => `linear-gradient(135deg, 
                            ${alpha(theme.palette.primary.main, 0.05)} 0%, 
                            ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                        opacity: 0.5,
                        zIndex: 0,
                    },
                }}
            >
                <Box
                    sx={{
                        fontSize: '6rem',
                        color: (theme) => alpha(theme.palette.primary.main, 0.2),
                        marginBottom: 3,
                        position: 'relative',
                        zIndex: 1,
                        animation: 'float 3s ease-in-out infinite',
                        '@keyframes float': {
                            '0%': {
                                transform: 'translateY(0px)',
                            },
                            '50%': {
                                transform: 'translateY(-10px)',
                            },
                            '100%': {
                                transform: 'translateY(0px)',
                            },
                        },
                    }}
                >
                    {hasActiveFilters ? <SearchOffIcon fontSize="inherit" /> : <LocalShippingIcon fontSize="inherit" />}
                </Box>

                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 600,
                        color: 'text.primary',
                        marginBottom: 1,
                        position: 'relative',
                        zIndex: 1,
                    }}
                >
                    {hasActiveFilters 
                        ? t('packages.emptyState.noPackagesFound')
                        : t('packages.emptyState.noPackages')}
                </Typography>

                <Typography
                    variant="body1"
                    sx={{
                        color: 'text.secondary',
                        maxWidth: '400px',
                        marginBottom: 3,
                        position: 'relative',
                        zIndex: 1,
                    }}
                >
                    {hasActiveFilters 
                        ? t('packages.emptyState.noPackagesFoundDescription')
                        : t('packages.emptyState.noPackagesDescription')}
                </Typography>
                
                {hasActiveFilters && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1, mb: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary" sx={{ width: '100%', textAlign: 'center', mb: 1 }}>
                            {t('packages.filters.activeFilters')}
                        </Typography>
                        {status && (
                            <Chip
                                icon={<FilterListIcon />}
                                label={`${t('packages.status.label')}: ${t(`packages.status.${status.toLowerCase()}`)}`}
                                onDelete={() => handleStatusChange({ target: { value: '' } } as any)}
                                sx={{
                                    margin: 0.5,
                                    background: (theme) => alpha(theme.palette.primary.main, 0.1),
                                    color: 'primary.main',
                                    border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                    '& .MuiChip-icon': {
                                        color: 'primary.main',
                                    },
                                }}
                            />
                        )}
                        {selectedSchool && (
                            <Chip
                                icon={<SchoolIcon />}
                                label={`${t('packages.school.label')}: ${schools.find((s: School) => s.id === selectedSchool)?.name || ''}`}
                                onDelete={() => handleSchoolChange({ target: { value: '' } } as any)}
                                sx={{
                                    margin: 0.5,
                                    background: (theme) => alpha(theme.palette.primary.main, 0.1),
                                    color: 'primary.main',
                                    border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                    '& .MuiChip-icon': {
                                        color: 'primary.main',
                                    },
                                }}
                            />
                        )}
                        {dateRange.start && dateRange.end && (
                            <Chip
                                icon={<CalendarTodayIcon />}
                                label={`${t('packages.dateRange.label')}: ${format(dateRange.start, 'MMM d, yyyy')} - ${format(dateRange.end, 'MMM d, yyyy')}`}
                                onDelete={() => {
                                    setDateRange({
                                        start: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
                                        end: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0),
                                    });
                                }}
                                sx={{
                                    margin: 0.5,
                                    background: (theme) => alpha(theme.palette.primary.main, 0.1),
                                    color: 'primary.main',
                                    border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                    '& .MuiChip-icon': {
                                        color: 'primary.main',
                                    },
                                }}
                            />
                        )}
                    </Box>
                )}
                
                {hasActiveFilters && (
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<FilterListIcon />}
                        onClick={() => {
                            setStatus('');
                            setSelectedSchool('');
                            setDateRange({
                                start: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
                                end: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0),
                            });
                        }}
                        sx={{ mt: 2 }}
                    >
                        {t('packages.emptyState.clearFilters')}
                    </Button>
                )}
            </Box>
        );
    };

    return (
        <Container maxWidth="xl" sx={{ py: 5 }}>
            <Typography 
                variant="h4" 
                component="h1" 
                gutterBottom
                sx={{
                    fontWeight: 700,
                    background: `linear-gradient(90deg, 
                        ${theme.palette.primary.main} 0%, 
                        ${theme.palette.secondary.main} 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 4,
                }}
            >
                {t('packages.title')}
            </Typography>

            <Box sx={filterContainerStyles(theme)}>
                <Box className="filter-header">
                    <FilterListIcon color="primary" />
                    <Typography variant="h6">
                        {t('packages.filters.title')}
                    </Typography>
                </Box>
                
                <Box className="filter-content">
                    <Box className="filter-group">
                        <FormControl>
                            <InputLabel>{t('packages.status.label')}</InputLabel>
                            <Select
                                value={status}
                                onChange={handleStatusChange}
                                label={t('packages.status.label')}
                                displayEmpty
                                renderValue={(value: string) => value === '' ? t('common.all') : t(`packages.status.${value.toLowerCase()}`)}
                                startAdornment={
                                    <InputAdornment position="start">
                                        <InventoryIcon color="action" />
                                    </InputAdornment>
                                }
                            >
                                <MenuItem value="">{t('common.all')}</MenuItem>
                                <MenuItem value="PENDING">{t('packages.status.pending')}</MenuItem>
                                <MenuItem value="PREPARED">{t('packages.status.prepared')}</MenuItem>
                                <MenuItem value="DELIVERED">{t('packages.status.delivered')}</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    <Box className="filter-group">
                        <FormControl>
                            <Autocomplete<School>
                                value={selectedSchool ? schools.find((school: School) => school.id === selectedSchool) || null : null}
                                onChange={(event, newValue: School | null) => {
                                    handleSchoolChange({ target: { value: newValue?.id || '' } } as any);
                                }}
                                options={schools}
                                getOptionLabel={(option: School) => option.name}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t('packages.school.label')}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start">
                                                        <SchoolIcon color="action" />
                                                    </InputAdornment>
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                loading={loadingSchools}
                                disabled={loadingSchools}
                                isOptionEqualToValue={(option: School, value: School) => option.id === value.id}
                                noOptionsText={t('common.noOptions')}
                                loadingText={t('common.loading')}
                                sx={{
                                    minWidth: 200,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: theme.spacing(1.5),
                                        transition: 'all 0.3s ease',
                                        backgroundColor: alpha(theme.palette.background.paper, 0.7),
                                        backdropFilter: 'blur(8px)',
                                        '&:hover': {
                                            backgroundColor: alpha(theme.palette.background.paper, 0.9),
                                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: alpha(theme.palette.primary.main, 0.5),
                                            },
                                        },
                                        '&.Mui-focused': {
                                            backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                            boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.15)}`,
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderWidth: 2,
                                                borderColor: theme.palette.primary.main,
                                            },
                                        },
                                    },
                                }}
                            />
                        </FormControl>
                    </Box>

                    <Box className="filter-group">
                        <DatePicker
                            label={t('packages.dateRange.start')}
                            value={dateRange.start}
                            onChange={(date: Date | null) => handleDateRangeChange('start', date)}
                            slotProps={{
                                textField: {
                                    InputProps: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <CalendarTodayIcon color="action" />
                                            </InputAdornment>
                                        ),
                                    },
                                }
                            }}
                        />
                    </Box>

                    <Box className="filter-group">
                        <DatePicker
                            label={t('packages.dateRange.end')}
                            value={dateRange.end}
                            onChange={(date: Date | null) => handleDateRangeChange('end', date)}
                            slotProps={{
                                textField: {
                                    InputProps: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <CalendarTodayIcon color="action" />
                                            </InputAdornment>
                                        ),
                                    },
                                }
                            }}
                        />
                    </Box>
                </Box>

                <Box className="filter-actions">
                    <Button
                        variant="outlined"
                        color="inherit"
                        startIcon={<CloseIcon />}
                        onClick={() => {
                            setStatus('');
                            setSelectedSchool('');
                            setDateRange({
                                start: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
                                end: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0),
                            });
                        }}
                        sx={{
                            borderColor: (theme) => alpha(theme.palette.divider, 0.2),
                            '&:hover': {
                                borderColor: (theme) => alpha(theme.palette.divider, 0.3),
                                backgroundColor: (theme) => alpha(theme.palette.action.hover, 0.05),
                            },
                        }}
                    >
                        {t('common.clearFilters')}
                    </Button>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {loading && !packages.length ? (
                <StyledBox>
                    <CircularProgress size={40} />
                    <Typography variant="h6" color="textSecondary">
                        {t('packages.table.loading')}
                    </Typography>
                </StyledBox>
            ) : packages.length === 0 ? (
                renderEmptyState()
            ) : (
                <>
                    <TableContainer 
                        component={Paper} 
                        sx={{
                            ...tableStyles,
                            position: 'relative',
                            opacity: loading ? 0.7 : 1,
                            transition: 'opacity 0.2s ease-in-out',
                        }}
                    >
                        {loading && (
                            <StyledOverlay>
                                <CircularProgress size={40} />
                            </StyledOverlay>
                        )}
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('packages.packageNumber')}</TableCell>
                                    <TableCell>{t('packages.status.label')}</TableCell>
                                    <TableCell>{t('packages.school.label')}</TableCell>
                                    <TableCell>{t('packages.studentId')}</TableCell>
                                    <TableCell>{t('packages.supplier')}</TableCell>
                                    <TableCell>{t('packages.deliveryDate')}</TableCell>
                                    <TableCell align="right">{t('packages.totalItems')}</TableCell>
                                    <TableCell align="right">{t('packages.totalAmount')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {packages.map((pkg: PackageType) => (
                                    <TableRow key={pkg.id}>
                                        <TableCell>{pkg.package_number}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={t(`packages.status.${pkg.status.toLowerCase()}`)}
                                                size="small"
                                                sx={(theme) => ({
                                                    backgroundColor: (() => {
                                                        switch (pkg.status as PackageStatus) {
                                                            case 'PREPARED':
                                                                return alpha(theme.palette.info.main, 0.15);
                                                            case 'DELIVERED':
                                                                return alpha(theme.palette.success.main, 0.15);
                                                            case 'PENDING':
                                                                return alpha(theme.palette.warning.main, 0.15);
                                                        }
                                                    })(),
                                                    color: (() => {
                                                        switch (pkg.status as PackageStatus) {
                                                            case 'PREPARED':
                                                                return theme.palette.info.main;
                                                            case 'DELIVERED':
                                                                return theme.palette.success.main;
                                                            case 'PENDING':
                                                                return theme.palette.warning.main;
                                                        }
                                                    })(),
                                                    fontWeight: 600,
                                                    '&:hover': {
                                                        transform: 'translateY(-2px)',
                                                        boxShadow: (() => {
                                                            switch (pkg.status as PackageStatus) {
                                                                case 'PREPARED':
                                                                    return `0 4px 12px ${alpha(theme.palette.info.main, 0.3)}`;
                                                                case 'DELIVERED':
                                                                    return `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}`;
                                                                case 'PENDING':
                                                                    return `0 4px 12px ${alpha(theme.palette.warning.main, 0.3)}`;
                                                            }
                                                        })(),
                                                    },
                                                })}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <SchoolIcon color="action" fontSize="small" />
                                                <Typography variant="body2">
                                                    {console.log('Rendering school:', pkg.school) || (pkg.school?.name || '-')}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>{pkg.student?.student_id || '-'}</TableCell>
                                        <TableCell>{pkg.supplier?.name || '-'}</TableCell>
                                        <TableCell>
                                            {pkg.delivery_date ? 
                                                format(new Date(pkg.delivery_date), 'PPP') : 
                                                '-'}
                                        </TableCell>
                                        <TableCell align="right">{pkg.total_items || 0}</TableCell>
                                        <TableCell align="right">
                                            {pkg.total_amount ? 
                                                Number(pkg.total_amount).toFixed(2) : 
                                                '0.00'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box 
                        sx={{ 
                            mt: 3,
                            display: 'flex',
                            justifyContent: 'center',
                            background: alpha(theme.palette.background.paper, 0.8),
                            borderRadius: theme.spacing(1),
                            backdropFilter: 'blur(8px)',
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        }}
                    >
                        <TablePagination
                            component="div"
                            count={totalPackages}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[20, 40, 60, 80]}
                            labelRowsPerPage={t('common.rowsPerPage')}
                            labelDisplayedRows={({ from, to, count }) => 
                                `${from}-${to} ${t('common.of')} ${count}`
                            }
                        />
                    </Box>
                </>
            )}
        </Container>
    );
}; 